import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Database,
  Fingerprint,
  Layers,
  Sparkles,
  Users,
  FileCheck,
} from "lucide-react";

/**
 * Phase 7a — Public landing page.
 *
 * Marketing surface only. No fake customer logos, no fake testimonials, no
 * benchmark statistics, no unsupported certification claims. The copy focuses
 * strictly on what RecoveryAssure actually does today.
 *
 * Conceptual separation (documented, not enforced by hostname in the MVP):
 *   recoveryassure.com    → this landing + /methodology + /privacy + /terms
 *   app.recoveryassure.com → authenticated SaaS at /w/:workspaceId/…
 *
 * Both are served from a single deployment for the MVP.
 */

const DEMO_MAILTO =
  "mailto:hello@recoveryassure.com?subject=Request%20a%20Demo%20%E2%80%94%20RecoveryAssure";

function Header() {
  return (
    <header
      data-testid="landing-header"
      className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur"
    >
      <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-brand">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-slate-900 text-white text-xs font-bold tracking-widest">
            RA
          </span>
          <span className="text-sm font-semibold">RecoveryAssure</span>
        </Link>
        <nav className="flex items-center gap-4 text-sm">
          <Link
            to="/methodology"
            className="text-slate-700 hover:text-slate-900"
            data-testid="landing-nav-methodology"
          >
            Methodology
          </Link>
          <Link
            to="/login"
            className="text-slate-700 hover:text-slate-900"
            data-testid="landing-nav-signin"
          >
            Sign in
          </Link>
          <a
            href={DEMO_MAILTO}
            data-testid="landing-nav-demo"
            className="inline-flex items-center gap-1.5 rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            Request a demo
          </a>
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section
      data-testid="landing-hero"
      className="mx-auto max-w-6xl px-6 pt-20 pb-24"
    >
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-slate-500">
          RecoveryAssure
        </div>
        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
          Your BIA says what the business needs. RecoveryAssure tells you
          whether you can actually recover.
        </h1>
        <p className="mt-6 text-base lg:text-lg text-slate-700 max-w-2xl leading-relaxed">
          A deterministic engine tests every recovery requirement against the
          technology, people, suppliers, and test evidence you already record.
          The engine is authoritative; AI is used only for interpretation, and
          every claim is traceable to the source cell it came from.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a
            href={DEMO_MAILTO}
            data-testid="landing-hero-demo"
            className="inline-flex items-center gap-2 rounded bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            Request a demo <ArrowRight size={14} />
          </a>
          <Link
            to="/login"
            data-testid="landing-hero-signin"
            className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-900 hover:bg-slate-50"
          >
            Sign in
          </Link>
        </div>
      </div>
    </section>
  );
}

function Problem() {
  return (
    <section
      data-testid="landing-problem"
      className="mx-auto max-w-6xl px-6 py-16 border-t border-slate-200"
    >
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-slate-500">
          The problem
        </div>
        <h2 className="mt-2 text-2xl sm:text-3xl font-semibold text-slate-900">
          Most BCM tools store RTOs, dependencies, strategies, tests. But
          inconsistencies can remain hidden.
        </h2>
        <p className="mt-4 text-slate-700 leading-relaxed">
          A recovery-time target of four hours on paper does not mean the
          application can be recovered in four hours. Suppliers, staffing,
          workarounds, and test evidence often quietly contradict the
          documented requirement. Auditors and regulators want defensible
          answers — spreadsheets alone cannot provide them.
        </p>
      </div>
    </section>
  );
}

function Solution() {
  const items = [
    { icon: FileCheck, title: "BIA quality", body: "Twenty deterministic rules validate coherence and credibility of your BIA data." },
    { icon: Layers, title: "Dependency conflicts", body: "Upstream / downstream RTAs are tested against every process they support." },
    { icon: ShieldCheck, title: "Recovery capability", body: "Nine-dimension feasibility model per process, separating claimed from demonstrated evidence." },
    { icon: Users, title: "Staffing & people", body: "Minimum-staff, key-person, cross-training checks — not just headcount totals." },
    { icon: Sparkles, title: "Workarounds", body: "Duration, capacity, tested-status, and MBCO coverage evaluated as a bridge, not a substitute." },
    { icon: Database, title: "Suppliers", body: "Contractual recovery targets compared against the process RTO of every dependent business function." },
    { icon: Fingerprint, title: "Demonstrated test results", body: "Latest DR test evidence is the source of truth — stale or missing tests degrade confidence explicitly." },
  ];
  return (
    <section
      data-testid="landing-solution"
      className="mx-auto max-w-6xl px-6 py-16 border-t border-slate-200"
    >
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-slate-500">
          What we analyse
        </div>
        <h2 className="mt-2 text-2xl sm:text-3xl font-semibold text-slate-900">
          The full stack of recovery evidence, one deterministic run.
        </h2>
      </div>
      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map(({ icon: Icon, title, body }) => (
          <article
            key={title}
            data-testid={`landing-solution-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="rounded-lg border border-slate-200 bg-white p-5"
          >
            <Icon size={18} className="text-slate-700" aria-hidden="true" />
            <h3 className="mt-3 text-sm font-semibold text-slate-900">
              {title}
            </h3>
            <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      num: "1",
      title: "Import your BIA data",
      body: "XLSX, CSV, or JSON. Read-only, data-only ingest — macros and OLE content are rejected before parsing. Ambiguous units are surfaced, never silently interpreted.",
    },
    {
      num: "2",
      title: "Deterministic analysis",
      body: "Twenty code-defined rules run against every process. Feasibility is derived from nine dimensions with explicit workaround bridges. Nothing is generated by an LLM.",
    },
    {
      num: "3",
      title: "AI interpretation (labelled)",
      body: "Where AI is used for phrasing recommendations or explaining findings, its output is labelled INFERRED and rejected if it contradicts the deterministic result.",
    },
    {
      num: "4",
      title: "Human review with evidence",
      body: "Every claim traces back to the source cell. Runs are immutable and content-hashed. Auditors and regulators get a defensible artefact, not opinions.",
    },
  ];
  return (
    <section
      data-testid="landing-howitworks"
      className="mx-auto max-w-6xl px-6 py-16 border-t border-slate-200"
    >
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-slate-500">
          How it works
        </div>
        <h2 className="mt-2 text-2xl sm:text-3xl font-semibold text-slate-900">
          Four steps from BIA workbook to defensible recovery assurance.
        </h2>
      </div>
      <ol className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {steps.map(({ num, title, body }) => (
          <li
            key={num}
            data-testid={`landing-step-${num}`}
            className="rounded-lg border border-slate-200 bg-white p-5 flex flex-col"
          >
            <span className="inline-flex h-9 w-9 items-center justify-center rounded bg-slate-900 text-white text-sm font-semibold">
              {num}
            </span>
            <h3 className="mt-4 text-sm font-semibold text-slate-900">{title}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Differentiation() {
  return (
    <section
      data-testid="landing-differentiation"
      className="bg-slate-900 text-slate-100 py-20"
    >
      <div className="mx-auto max-w-6xl px-6 grid gap-10 lg:grid-cols-[2fr_3fr] items-start">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">
            Differentiation
          </div>
          <h2 className="mt-2 text-2xl sm:text-3xl font-semibold">
            Not another BCM platform.
          </h2>
          <p className="mt-4 text-slate-300 leading-relaxed">
            RecoveryAssure works with the BCM information you already have —
            spreadsheets, application catalogues, DR test records — and answers
            the question that BCM platforms leave to auditors: does the recovery
            you documented actually hold up?
          </p>
        </div>
        <ul className="space-y-4 text-sm text-slate-200">
          {[
            "Deterministic engine — every finding is code-defined and versioned.",
            "Immutable analysis runs — content hashed, reproducible byte-for-byte.",
            "Content-hashed audit receipts — tamper-evident, exportable to auditors.",
            "Transparent methodology — the pipeline is public and documented.",
            "AI is labelled and constrained — it never overrides the engine.",
          ].map((line) => (
            <li key={line} data-testid={`landing-diff-bullet`} className="flex gap-3">
              <ShieldCheck size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-emerald-400" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function TargetUsers() {
  return (
    <section
      data-testid="landing-users"
      className="mx-auto max-w-6xl px-6 py-16 border-t border-slate-200"
    >
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-slate-500">
          Who this is for
        </div>
        <h2 className="mt-2 text-2xl sm:text-3xl font-semibold text-slate-900">
          Regulated enterprises and the consultants who serve them.
        </h2>
      </div>
      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-slate-900">
            Enterprises with existing BCM
          </h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            Financial services, healthcare, utilities, and critical
            infrastructure operators who already run a BCM programme and
            need defensible answers to the question of whether recovery actually holds up.
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-slate-900">
            BCM consultants
          </h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            Practitioners engaged to review, remediate, or audit a client&apos;s
            business continuity programme — RecoveryAssure produces the
            evidence trail your engagement report can cite.
          </p>
        </div>
      </div>
    </section>
  );
}

function Trust() {
  return (
    <section
      data-testid="landing-trust"
      className="mx-auto max-w-6xl px-6 py-16 border-t border-slate-200"
    >
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-slate-500">
          Trust
        </div>
        <h2 className="mt-2 text-2xl sm:text-3xl font-semibold text-slate-900">
          Every finding is defensible. Every fact is traceable. The engine, not
          the AI, is authoritative.
        </h2>
        <p className="mt-4 text-slate-700 leading-relaxed">
          RecoveryAssure separates BIA quality from recovery feasibility, and
          both from validation confidence. Deterministic and AI outputs are
          rendered in different visual layers so a reviewer never confuses
          interpretation with truth.
        </p>
        <Link
          to="/methodology"
          data-testid="landing-methodology-link"
          className="mt-6 inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50"
        >
          Read the methodology <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}

function FooterCTAs() {
  return (
    <section
      data-testid="landing-footer-cta"
      className="border-t border-slate-200 bg-slate-50"
    >
      <div className="mx-auto max-w-6xl px-6 py-12 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900">
            Ready to test your recovery assurance?
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            30-minute walkthrough on your own BIA data — pilot workspace on the house.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <a
            href={DEMO_MAILTO}
            data-testid="landing-footer-demo"
            className="inline-flex items-center gap-2 rounded bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            Request a demo <ArrowRight size={14} />
          </a>
          <Link
            to="/login"
            data-testid="landing-footer-signin"
            className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-900 hover:bg-slate-50"
          >
            Sign in
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer
      data-testid="landing-footer"
      className="bg-slate-900 text-slate-400 text-xs"
    >
      <div className="mx-auto max-w-6xl px-6 py-8 flex flex-wrap items-center justify-between gap-3">
        <div>© {new Date().getFullYear()} RecoveryAssure. All rights reserved.</div>
        <nav className="flex items-center gap-4">
          <Link to="/methodology" className="hover:text-slate-100">
            Methodology
          </Link>
          <Link to="/privacy" className="hover:text-slate-100" data-testid="landing-footer-privacy">
            Privacy
          </Link>
          <Link to="/terms" className="hover:text-slate-100" data-testid="landing-footer-terms">
            Terms
          </Link>
        </nav>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
      <Header />
      <main>
        <Hero />
        <Problem />
        <Solution />
        <HowItWorks />
        <Differentiation />
        <TargetUsers />
        <Trust />
        <FooterCTAs />
      </main>
      <Footer />
    </div>
  );
}
